/**
 * UserLookupBody.js
 */

import React, {useContext, useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import {labels, GetCardTypeForBody} from '../../../../../util/utils';
import {
	UserLookupType,
	UserERPTypeId,
	roles,
	engagementUserStatus,
	userSearchMaxLength
} from '../../../../../util/uiconstants';
import {DocumentTypeIdContext} from '../../../../Main/Forms/CanvasFormContainer';
import {createRequestURL, createUpdatePayload} from '../Common/BodyResponseUtility';
import {getUserByGUI} from '../../../../../actions/User/useractions';
import {
	getInternalUsers,
	patchDeactivateInternalUsers,
	saveInternalUser
} from '../../../../../actions/InternalUsers/internalusersactions';
import BodyKnowledgeDescription from '../BodyKnowledgeDescription';
import UserSearch from '../../../Search/usersearch';
import _ from 'lodash';
import {useDocumentId, useEngagementId} from '../../../../../util/customHooks';

export default function UserLookupBody(props) {
	const {
		isAresView,
		showGuidance,
		guidanceFilter,
		options,
		headerId,
		sectionId,
		bodyId,
		updateBodyOptions,
		getBodiesBySectionId,
		isSnapshotView
	} = props;

	const maxLength = userSearchMaxLength;
	const dispatch = useDispatch();

	const formBodyBorder = GetCardTypeForBody(options, isAresView);

	const userLookupPlaceholder = isAresView ? labels.userLookupInstructionalText : labels.selectTeamMember;

	const [teamMember, setTeamMember] = useState({value: userLookupPlaceholder}),
		[userInformation, setUserInformation] = useState({}),
		[isLoading, setIsLoading] = useState(false),
		[isSaving, setIsSaving] = useState(false),
		[isRemoving, setIsRemoving] = useState(false),
		[isWaitingForUsers, setIsWaitingForUsers] = useState(false),
		[hideClearAllButton, setHideClearAllButton] = useState(true);

	const engagementId = useEngagementId(),
		documentTypeId = useContext(DocumentTypeIdContext),
		documentId = useDocumentId();

	const userDetail = useSelector((state) => state.userDetail?.byId[options.bodyFreeText]),
		engagementUsers = useSelector((state) => state.internalUsers);

	const saveBodyOption = (gui) => {
		if (gui) {
			setIsSaving(true);
		} else {
			setIsRemoving(true);
		}

		const bodyResponseObject = createUpdatePayload(options?.bodyTypeId, gui);
		updateBodyOptions(
			bodyId,
			0,
			createRequestURL(engagementId, documentId, headerId, sectionId, bodyId, documentTypeId),
			bodyResponseObject
		)
			.then(() => {
				setIsSaving(false);
				setIsRemoving(false);
			})
			.catch(() => {
				setIsSaving(false);
				setIsRemoving(false);
			});
	};

	const postUser = () => {
		setIsSaving(true);
		const userInitial = userInformation.firstName.substring(0, 1) + userInformation.lastName.substring(0, 1);

		let userData = {
			objectId: userInformation.objectId,
			firstName: userInformation.firstName,
			lastName: userInformation.lastName,
			userInitials: userInitial,
			email: userInformation.email,
			displayName: userInformation.displayName,
			userPrincipalName: userInformation.userPrincipalName,
			gui: userInformation.gui,
			gpn: userInformation.gpn,
			preferredContactPhone: userInformation.preferredContactPhone,
			eyRole: userInformation.jobTitle,
			employmentStatus: userInformation.employmentStatus,
			userType: UserERPTypeId.Internal,
			userRoleIds: [],
			engagementRoleID: roles.SENIOR,
			engagementUserFirstName: userInformation.firstName,
			engagementUserLastName: userInformation.lastName,
			engagementUserInitials: userInitial
		};

		dispatch(saveInternalUser(engagementId, userData))
			.then((user) => {
				const data = createDeactivatePatchModal(user);
				dispatch(patchDeactivateInternalUsers(engagementId, data));
				setIsSaving(false);
				saveBodyOption(userInformation.gui);
			})
			.catch(() => {
				setIsSaving(false);
			});
	};

	const createDeactivatePatchModal = (user) => {
		let data = [];
		data.push({
			op: 'replace',
			value: engagementUserStatus.DeActivated,
			path: `/${user.engagementUsers[0].userID}/EngagementUserStatusID`
		});
		return data;
	};

	const getAllInternalUsers = () => {
		if (!engagementUsers || !engagementUsers.allIds || engagementUsers.allIds.length === 0) {
			setIsSaving(true);
			const userStatuses = [
				engagementUserStatus.Active,
				engagementUserStatus.Pending,
				engagementUserStatus.DeActivated,
				engagementUserStatus.ScheduledInactive
			];

			const pageSize = -1,
				page = 1;

			setIsWaitingForUsers(true);
			dispatch(getInternalUsers(engagementId, userStatuses, undefined, pageSize, page)).then(() => {
				setIsSaving(false);
				setIsWaitingForUsers(false);
			});
		}
	};

	const onUserClear = () => {
		const teamMember = {value: userLookupPlaceholder};
		setHideClearAllButton(true);
		setTeamMember(teamMember);
		saveBodyOption('');
	};

	const onUserSelectionChange = (user) => {
		setHideClearAllButton(false);
		setUserInformation(user.allProps);
	};

	const getUserDetails = (gui) => {
		dispatch(getUserByGUI(engagementId, gui)).catch(() => {
			setIsLoading(false);
		});
	};

	useEffect(() => {
		if (options.bodyFreeText) {
			setIsLoading(true);
			getUserDetails(options.bodyFreeText);
		}
	}, [options.bodyFreeText]);

	useEffect(() => {
		let teamMember = {},
			hideClearAllButton = true;
		if (options.bodyFreeText && userDetail && !_.isEmpty(userDetail)) {
			hideClearAllButton = false;
			teamMember = {
				allProps: {
					firstName: userDetail.firstName,
					lastName: userDetail.lastName,
					email: userDetail.email
				},
				label: userDetail.firstName + userDetail.lastName,
				value: userDetail.email
			};
			setIsLoading(false);
		} else {
			hideClearAllButton = true;
			teamMember = {value: userLookupPlaceholder};
		}
		setHideClearAllButton(hideClearAllButton);
		setTeamMember(teamMember);
	}, [userDetail]);

	useEffect(() => {
		if (userInformation && userInformation.gui) {
			getAllInternalUsers();
		}
	}, [userInformation]);

	useEffect(() => {
		if (userInformation?.gui && !isWaitingForUsers && engagementUsers?.allIds && engagementUsers?.allIds.length > 0) {
			if (engagementUsers.allIds.includes(userInformation.gui)) {
				/*if user already exists in the engagement then add the user to the body*/
				saveBodyOption(userInformation.gui);
			} else {
				postUser();
			}
			setIsWaitingForUsers(false);
		}
	}, [engagementUsers, userInformation]);

	return (
		<Wrapper className={`SectionBodyWrapper ${formBodyBorder}`}>
			{(isAresView || options?.optionsAndResponses?.length > 0) && (
				<BodyKnowledgeDescription
					isAresView={isAresView}
					showGuidance={showGuidance}
					guidanceFilter={guidanceFilter}
					isSnapshotView={isSnapshotView}
					bodyModel={options}
					engagementId={engagementId}
					documentId={documentId}
					headerId={headerId}
					sectionId={sectionId}
					bodyId={bodyId}
					bodyDescriptionText={isAresView ? '' : options.name}
					isCustom={options.isCustom}
					isDuplicate={options.isDuplicate}
					getBodiesBySectionId={getBodiesBySectionId}
				/>
			)}
			<section className="SectionBodyData SectionUserSearchBodyData" id={bodyId}>
				<UserSearch
					key={`${headerId}-${sectionId}-${bodyId}`}
					viewKey={bodyId}
					onUserClear={onUserClear}
					onUserSelectionChange={onUserSelectionChange}
					lookupType={UserLookupType.Internal}
					teamMember={teamMember}
					isLoading={isLoading}
					setIsLoading={setIsLoading}
					isSaving={isSaving}
					isRemoving={isRemoving}
					hideClearAllButton={hideClearAllButton}
					maxLength={maxLength}
					instructionalText={labels.userLookupInstructionalText}
					isAresView={isAresView}
				/>
			</section>
		</Wrapper>
	);
}

const Wrapper = styled.section`
	display: block;
	.SectionBodyData {
		position: relative;
		&.SectionUserSearchBodyData {
			.user-input-wrapper {
				width: var(--px-480);
			}
			.nameandemail {
				flex-direction: unset !important;
			}
			.react-select-container {
				.react-select__control {
					border: var(--px-1) solid var(--neutrals-700);
					.react-select__single-value {
						padding-top: var(--px-5);
					}
					.react-select__indicators {
						display: none;
					}
				}
			}
		}
	}
`;
